import { Box, Stack, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TruncateTooltip from 'commonComponent/TruncateTooltip';
import { ThemeMode } from 'config';
import {
  CONTEXT_TYPE_MY_INSIGHT,
  getAccountContextTitle,
  getAccountInitials,
  formatAccountContextRoleLabel,
} from 'utils/accountContext/accountContextUtils';

/** Figma tokens for account rows (Select Account + Switch Accounts). */
export const ACCOUNT_ROW = {
  border: '#E0E0E0',
  borderSelected: '#26A69A',
  bgSelected: '#E0F2F1',
  title: '#212121',
  role: '#757575',
  chevron: '#BDBDBD',
  radius: '8px',
  gap: 12,
};

/**
 * Shared account row for post-login Select Account and header Switch Accounts.
 * Styled to match Figma: initials avatar, bold name, muted role, chevron/check.
 */
export default function AccountContextListItem({
  context,
  intl,
  selected = false,
  showChevron = false,
  showCheck = false,
  disabled = false,
  onSelect,
  dense = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === ThemeMode.DARK;
  const title = getAccountContextTitle(context, intl);
  const roleLabel = formatAccountContextRoleLabel(intl, context);
  const initials = getAccountInitials(title);
  const avatarSize = 36;
  const isMyInsight = context?.contextType === CONTEXT_TYPE_MY_INSIGHT;

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-current={selected ? 'true' : undefined}
      data-testid={`account-context-item-${context?.id}`}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: dense ? 1.25 : 1.5,
        px: dense ? 1.5 : 2,
        py: dense ? 1.25 : 1.5,
        minHeight: dense ? 52 : 56,
        borderRadius: ACCOUNT_ROW.radius,
        border: '1px solid',
        borderColor: selected
          ? ACCOUNT_ROW.borderSelected
          : isDark
            ? 'divider'
            : ACCOUNT_ROW.border,
        bgcolor: selected
          ? isDark
            ? alpha(theme.palette.teal[500], 0.14)
            : ACCOUNT_ROW.bgSelected
          : isDark
            ? 'background.paper'
            : 'common.white',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        boxSizing: 'border-box',
        '&:hover': disabled
          ? undefined
          : {
              borderColor: selected
                ? ACCOUNT_ROW.borderSelected
                : isDark
                  ? 'grey.600'
                  : 'grey.400',
              bgcolor: selected
                ? isDark
                  ? alpha(theme.palette.teal[500], 0.18)
                  : alpha(ACCOUNT_ROW.bgSelected, 0.9)
                : isDark
                  ? alpha(theme.palette.common.white, 0.04)
                  : 'grey.50',
            },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: '50%',
          bgcolor: 'bluegrey.800',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.02em',
          lineHeight: 1,
          fontFamily: theme.typography.fontFamily,
        }}
        data-testid={isMyInsight ? 'account-avatar-my-insight' : undefined}
      >
        {initials}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, py: 0.25, overflow: 'hidden' }}>
        <TruncateTooltip
          sx={{
            maxWidth: '100%',
            color: isDark ? 'text.primary' : ACCOUNT_ROW.title,
            fontWeight: 600,
            fontSize: dense ? 13 : 14,
            lineHeight: 1.3,
            letterSpacing: 0,
            display: 'block',
          }}
        >
          {title}
        </TruncateTooltip>
        {roleLabel && (
          <Typography
            noWrap
            sx={{
              color: isDark ? 'text.secondary' : ACCOUNT_ROW.role,
              fontWeight: 400,
              fontSize: dense ? 12 : 12,
              lineHeight: 1.3,
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {roleLabel}
          </Typography>
        )}
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0, ml: 0.5 }}>
        {showCheck && selected && (
          <CheckIcon
            sx={{ fontSize: 20, color: ACCOUNT_ROW.borderSelected }}
            aria-label={intl.formatMessage({
              id: 'accountContextSelected',
              defaultMessage: 'Selected',
            })}
          />
        )}
        {showChevron && (
          <ChevronRightIcon
            sx={{
              fontSize: 22,
              color: isDark ? 'grey.500' : ACCOUNT_ROW.chevron,
            }}
          />
        )}
      </Stack>
    </Box>
  );
}
